import {Card} from 'antd';
import React, {FunctionComponent, useEffect, useState} from 'react';

import {Form} from 'components/form/form';
import {EFormMode} from 'components/form/form-types';
import {Spinner} from 'components/spinner';
import {
    IDynamicTitle, loadMetadata, resetMetadata, selectMetadata, IUrlConfig,
} from 'entities/metadata';
import {selectContextRawData} from 'modules/context/context-selector';
import {TEntity, resetLoadedData, selectEntityData} from 'modules/data';
import {
    loadEditFormData,
    performActionForForm,
    resetFormData,
    resetFormUsedAdditionalOptions,
} from 'modules/data/data-actions';
import {IFormEntityData} from 'modules/data/data-types';
import {EEntityType} from 'shared/constants/entities';
import {TStateSetter} from 'shared/types/generics';
import {ERequestType} from 'shared/types/requests';
import {combineString} from 'shared/utils/combine-string';
import {useAppDispatch, useAppSelector} from 'store/config/hooks';
import {controlRatioSelectors} from 'store/slices/control-ratio-slice';

interface IOwnProps {
    entityName: string;
    entityType?: EEntityType;
    url: string;
    resetEntityName?: string;
    resetEntityType?: EEntityType;
    onClose?: () => void;
    setShouldResetTable?: TStateSetter<boolean>;
}

export const FormEditContent: FunctionComponent<IOwnProps> = ({
    entityName,
    entityType = EEntityType.FORM,
    url,
    resetEntityName,
    onClose,
    setShouldResetTable,
    resetEntityType,
}: IOwnProps) => {
    const dispatch = useAppDispatch();
    const metadataSelector = selectMetadata(entityName, entityType);
    const dataSelector = selectEntityData(entityName, EEntityType.FORM);
    const metadataTableSelector = selectMetadata(entityName, EEntityType.TABLE);

    const metadata = useAppSelector(state => metadataSelector(state));
    const metadataTable = useAppSelector(state => metadataTableSelector(state));
    const formData = useAppSelector(state => dataSelector(state)) as IFormEntityData;
    const contextData = useAppSelector(selectContextRawData);
    const modalParameters = useAppSelector(controlRatioSelectors.selectFormAdditionalParameters);
    const [isEditable, setIsEditable] = useState(false);

    const actionForForm = (
        // eslint-disable-next-line @typescript-eslint/no-shadow
        entityName: string,
        referenceUrl: string,
        requestType: ERequestType,
        newData: Record<string, any>,
        isJsonRequest?: boolean,
        urlParamKey?: string,
        additionalData?: TEntity,
        urlConfig?: IUrlConfig,
    ) => dispatch(performActionForForm(entityType)(
        entityName, referenceUrl, requestType, newData, isJsonRequest, urlParamKey, additionalData, urlConfig,
    ));
    const onHandleClose = () => {
        if (onClose) {
            onClose();
        }
    };

    useEffect(() => {
        if (!metadata) {
            dispatch(loadMetadata(entityName, entityType));
        } else {
            // если нет кнопки сохранения (вероятно удалена по ролевой) -> FormMode.REPRESENTATION
            setIsEditable(() => {
                if (metadata?.actions) {
                    return metadata.actions.some(action => action.actionCode === 'CREATE');
                }
                return false;
            });
        }
        if (!formData?.initialData && metadata) {
            dispatch(loadEditFormData(entityName, url, metadata?.prefix, metadata?.urlConfig));
        }
        if (metadataTable?.useDifferentForms) {
            onHandleClose();
        }
    }, [metadata]);

    // data cleanup on unmount
    useEffect(() => () => {
        dispatch(resetFormData({entityName}));
        dispatch(resetFormUsedAdditionalOptions({entityName}));
        dispatch(resetMetadata({entityName, entityType, loading: false}));
        if (resetEntityName && resetEntityType) {
            dispatch(resetLoadedData(resetEntityName, resetEntityType));
        }
    }, []);

    const dynamicTitleOptions: IDynamicTitle = metadata?.dynamicFormTitle as IDynamicTitle;

    const cardTitle = (dynamicTitleOptions?.edit
        && combineString(dynamicTitleOptions.edit, formData?.data)) || dynamicTitleOptions?.edit;

    const isLoaded = !!metadata && !!formData && formData.initialData;
    return isLoaded ? (
        <Form
            mode={isEditable ? EFormMode.EDIT : EFormMode.REPRESENTATION}
            cardTitle={cardTitle}
            formData={formData}
            meta={metadata}
            entityName={entityName}
            onClose={onHandleClose}
            actionForForm={actionForForm}
            additionalValuesForRequest={modalParameters || undefined}
            contextData={contextData}
            setShouldResetTable={setShouldResetTable}
        />
    ) : <Card style={{paddingTop: 20}}> <Spinner /></Card>;
};
